import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Award, User, Mail, Phone, Calendar, MapPin, Briefcase, FileText, Upload, CheckCircle, AlertCircle, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExaminerApplyPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    photo_url: '',
    qualifications: [''],
    specializations: [],
    experience_years: '',
    current_organization: '',
    designation: '',
    address: '',
    professional_registration_number: '',
    issuing_authority: '',
    cv_url: '',
    motivation: '',
    declaration_accepted: false
  });

  const specializations = [
    'Aesthetic Medicine',
    'Dermatology',
    'Plastic Surgery',
    'Longevity Medicine',
    'Regenerative Medicine',
    'Aesthetic Dentistry',
    'Aesthetic Nursing',
    'Clinical Assistance'
  ];

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('CV must be less than 10MB');
        return;
      }
      setCvFile(file);
    }
  };

  const addQualification = () => {
    setForm({...form, qualifications: [...form.qualifications, '']});
  };

  const updateQualification = (index, value) => {
    const newQuals = [...form.qualifications];
    newQuals[index] = value;
    setForm({...form, qualifications: newQuals});
  };

  const removeQualification = (index) => {
    const newQuals = form.qualifications.filter((_, i) => i !== index);
    setForm({...form, qualifications: newQuals.length ? newQuals : ['']});
  };

  const toggleSpecialization = (spec) => {
    const current = form.specializations;
    if (current.includes(spec)) {
      setForm({...form, specializations: current.filter(s => s !== spec)});
    } else {
      setForm({...form, specializations: [...current, spec]});
    }
  };

  const validateStep1 = () => {
    if (!form.full_name || !form.email || !form.phone || !form.date_of_birth || !form.gender || !form.nationality) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (!photoFile && !form.photo_url) {
      toast.error('Please upload your passport-size photo');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const filledQuals = form.qualifications.filter(q => q.trim());
    if (filledQuals.length === 0) {
      toast.error('Please add at least one qualification');
      return false;
    }
    if (form.specializations.length === 0) {
      toast.error('Please select at least one specialization');
      return false;
    }
    if (!form.experience_years || !form.current_organization || !form.designation) {
      toast.error('Please fill all required fields');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!form.professional_registration_number || !form.issuing_authority || !form.address) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (!form.declaration_accepted) {
      toast.error('Please accept the declaration to proceed');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setIsSubmitting(true);
    try {
      // Upload photo first if needed
      let photoUrl = form.photo_url;
      if (photoFile && !photoUrl) {
        const formData = new FormData();
        formData.append('file', photoFile);
        const uploadRes = await axios.post(`${API}/upload`, formData);
        photoUrl = uploadRes.data.url;
      }
      
      // Upload CV if provided
      let cvUrl = form.cv_url;
      if (cvFile && !cvUrl) {
        const formData = new FormData();
        formData.append('file', cvFile);
        const uploadRes = await axios.post(`${API}/upload`, formData);
        cvUrl = uploadRes.data.url;
      }
      
      // Submit application
      const applicationData = {
        ...form,
        photo_url: photoUrl,
        cv_url: cvUrl,
        qualifications: form.qualifications.filter(q => q.trim()),
        experience_years: parseInt(form.experience_years) || 0
      };
      
      const res = await axios.post(`${API}/examiner-applications`, applicationData);
      setApplicationId(res.data.application_id);
      setStep(4); // Success step
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-12">
        <div className="imcac-container text-center">
          <Badge className="bg-[#C5A572] text-white mb-4">EXAMINER APPLICATION</Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 font-heading">
            Join IMCAC as an Examiner
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Become part of the International Medical Competency Accreditation Council's panel of esteemed examiners.
            Help shape the future of aesthetic medicine certification.
          </p>
        </div>
      </section>

      <div className="imcac-container py-12">
        {/* Progress Steps */}
        {step < 4 && (
          <div className="flex items-center justify-center mb-8 gap-2" data-testid="progress-steps">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step >= s ? 'bg-[#1B2A4A] text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                </div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-[#1B2A4A]' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Personal Information */}
        {step === 1 && (
          <Card className="max-w-2xl mx-auto" data-testid="step-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-[#C5A572]" /> Personal Information
              </CardTitle>
              <CardDescription>Please provide your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo Upload */}
              <div className="flex items-start gap-4">
                <div className="w-24 h-28 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center p-2">
                      <Upload className="h-6 w-6 mx-auto text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Photo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Label>Passport Size Photo *</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">JPG or PNG, max 5MB. White background preferred.</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={form.full_name}
                    onChange={e => setForm({...form, full_name: e.target.value})}
                    placeholder="Dr. John Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="john.smith@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={form.date_of_birth}
                    onChange={e => setForm({...form, date_of_birth: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nationality *</Label>
                  <Input
                    value={form.nationality}
                    onChange={e => setForm({...form, nationality: e.target.value})}
                    placeholder="Indian"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={nextStep} className="bg-[#1B2A4A] text-white">
                  Continue to Qualifications
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Professional Qualifications */}
        {step === 2 && (
          <Card className="max-w-2xl mx-auto" data-testid="step-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-[#C5A572]" /> Professional Qualifications
              </CardTitle>
              <CardDescription>Your medical/professional qualifications and experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Qualifications */}
              <div>
                <Label>Qualifications *</Label>
                {form.qualifications.map((qual, i) => (
                  <div key={i} className="flex gap-2 mt-2">
                    <Input
                      value={qual}
                      onChange={e => updateQualification(i, e.target.value)}
                      placeholder="e.g., MBBS, MD Dermatology"
                    />
                    {form.qualifications.length > 1 && (
                      <Button variant="outline" size="icon" onClick={() => removeQualification(i)}>
                        ×
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addQualification} className="mt-2">
                  + Add Another Qualification
                </Button>
              </div>
              
              {/* Specializations */}
              <div>
                <Label>Specializations * (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {specializations.map((spec) => (
                    <div
                      key={spec}
                      className={`p-3 border rounded-sm cursor-pointer transition-colors ${
                        form.specializations.includes(spec)
                          ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]'
                          : 'bg-white hover:bg-slate-50 border-slate-200'
                      }`}
                      onClick={() => toggleSpecialization(spec)}
                    >
                      <span className="text-sm">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Years of Experience *</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.experience_years}
                    onChange={e => setForm({...form, experience_years: e.target.value})}
                    placeholder="10"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Current Organization *</Label>
                  <Input
                    value={form.current_organization}
                    onChange={e => setForm({...form, current_organization: e.target.value})}
                    placeholder="Apollo Hospital, Mumbai"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Current Designation *</Label>
                  <Input
                    value={form.designation}
                    onChange={e => setForm({...form, designation: e.target.value})}
                    placeholder="Senior Consultant, Aesthetic Medicine"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* CV Upload */}
              <div>
                <Label>Upload CV/Resume (Optional)</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleCvChange}
                  className="mt-1"
                />
                {cvFile && <p className="text-xs text-green-600 mt-1">File selected: {cvFile.name}</p>}
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={nextStep} className="bg-[#1B2A4A] text-white">
                  Continue to Registration
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Professional Registration & Declaration */}
        {step === 3 && (
          <Card className="max-w-2xl mx-auto" data-testid="step-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-[#C5A572]" /> Professional Registration
              </CardTitle>
              <CardDescription>Your medical registration and declaration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Professional Registration Number *</Label>
                  <Input
                    value={form.professional_registration_number}
                    onChange={e => setForm({...form, professional_registration_number: e.target.value})}
                    placeholder="MCI/2010/12345"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Issuing Authority *</Label>
                  <Input
                    value={form.issuing_authority}
                    onChange={e => setForm({...form, issuing_authority: e.target.value})}
                    placeholder="Medical Council of India"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Full Address *</Label>
                <Textarea
                  value={form.address}
                  onChange={e => setForm({...form, address: e.target.value})}
                  placeholder="Enter your complete address with city, state, and pin code"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Why do you want to become an IMCAC Examiner? (Optional)</Label>
                <Textarea
                  value={form.motivation}
                  onChange={e => setForm({...form, motivation: e.target.value})}
                  placeholder="Share your motivation and what you can bring to IMCAC..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              {/* Declaration */}
              <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                <h4 className="font-semibold text-[#1B2A4A] mb-2">Declaration</h4>
                <p className="text-sm text-slate-600 mb-4">
                  I hereby declare that all information provided in this application is true and accurate to the best of my knowledge.
                  I understand that providing false information may result in rejection of my application or revocation of my examiner status.
                  I agree to abide by the IMCAC Code of Ethics and Examiner Guidelines if appointed.
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="declaration"
                    checked={form.declaration_accepted}
                    onCheckedChange={(checked) => setForm({...form, declaration_accepted: checked})}
                  />
                  <Label htmlFor="declaration" className="text-sm cursor-pointer">
                    I accept the above declaration *
                  </Label>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button 
                  onClick={handleSubmit} 
                  className="bg-[#C5A572] text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Card className="max-w-lg mx-auto text-center" data-testid="step-4-success">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2">Application Submitted!</h2>
              <p className="text-slate-600 mb-4">
                Thank you for your interest in becoming an IMCAC Examiner. Your application has been received and is under review.
              </p>
              
              <div className="bg-slate-50 p-4 rounded-sm mb-6">
                <p className="text-sm text-slate-500">Application ID</p>
                <p className="text-lg font-mono font-semibold text-[#1B2A4A]">{applicationId}</p>
              </div>
              
              <div className="text-left bg-blue-50 p-4 rounded-sm mb-6">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4" /> What happens next?
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Your application will be reviewed by our team</li>
                  <li>• You may be contacted for additional verification</li>
                  <li>• Approved examiners undergo calibration training</li>
                  <li>• Decision notification within 10-15 business days</li>
                </ul>
              </div>
              
              <div className="flex gap-2 justify-center">
                <Link to="/">
                  <Button variant="outline">Back to Home</Button>
                </Link>
                <Link to="/login/examiner">
                  <Button className="bg-[#1B2A4A] text-white">Examiner Login</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
